import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Modal } from "antd";
import { IconPrinter, IconSignature, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { useSignatureUnlock } from "@/lib/signature/useSignatureUnlock";
import {
  type PreviewMargin,
  type StampLabels,
  injectIntoHead,
  placeStamp,
  stampStyles,
} from "@/lib/signature/printStamps";

const OVERLAY_Z = 1000;
const MODAL_Z = 1100;
const TAP_SLOP = 6;

export interface SignPrintPreviewProps {
  open: boolean;
  title: string;
  html: string;
  margin: PreviewMargin;
  pageWidthMm?: number;
  onClose: () => void;
}

function SignPrintPreview({ open, title, html, margin, pageWidthMm = 210, onClose }: SignPrintPreviewProps) {
  const { t } = useTranslation();
  const { signature, request, modal } = useSignatureUnlock({ zIndex: MODAL_Z });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(1123);
  const [ready, setReady] = useState(false);
  const [dragGhost, setDragGhost] = useState<{ x: number; y: number } | null>(null);

  const srcDoc = useMemo(
    () => injectIntoHead(html, `<style data-sign-print>${stampStyles(margin)}</style>`),
    [html, margin],
  );

  const labels: StampLabels = useMemo(
    () => ({
      remove: t("signPrint.removeSignature") || "移除簽名",
      resize: t("signPrint.resizeSignature") || "調整簽名大小",
      image: t("signPrint.signature") || "簽名",
    }),
    [t],
  );

  useEffect(() => {
    if (!open) setReady(false);
  }, [open]);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    const win = iframeRef.current?.contentWindow as (Window & typeof globalThis) | null;
    if (!doc || !win) return;
    const measure = () => setHeight(Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0));
    measure();
    if (win.ResizeObserver && doc.body) new win.ResizeObserver(measure).observe(doc.body);
    setReady(true);
  }, []);

  const placeAtClient = (clientX: number, clientY: number, image: string) => {
    const frame = iframeRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return false;
    const rect = frame.getBoundingClientRect();
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return false;
    placeStamp(doc, image, clientX - rect.left, clientY - rect.top, labels);
    return true;
  };

  const placeInView = (image: string) => {
    const frame = iframeRef.current;
    const scroller = scrollRef.current;
    if (!frame || !scroller) return;
    const frameRect = frame.getBoundingClientRect();
    const viewRect = scroller.getBoundingClientRect();
    const x = frameRect.left + frameRect.width / 2;
    const top = Math.max(frameRect.top, viewRect.top);
    const bottom = Math.min(frameRect.bottom, viewRect.bottom);
    placeAtClient(x, (top + bottom) / 2, image);
  };

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!signature) return;
    e.preventDefault();
    const chip = e.currentTarget;
    try {
      chip.setPointerCapture(e.pointerId);
    } catch {}
    const startX = e.clientX;
    const startY = e.clientY;
    const image = signature;
    let moved = false;
    const move = (ev: PointerEvent) => {
      if (Math.abs(ev.clientX - startX) > TAP_SLOP || Math.abs(ev.clientY - startY) > TAP_SLOP) moved = true;
      if (moved) setDragGhost({ x: ev.clientX, y: ev.clientY });
    };
    const end = (ev: PointerEvent) => {
      chip.removeEventListener("pointermove", move);
      chip.removeEventListener("pointerup", end);
      chip.removeEventListener("pointercancel", cancel);
      setDragGhost(null);
      if (moved) placeAtClient(ev.clientX, ev.clientY, image);
      else placeInView(image);
    };
    const cancel = () => {
      chip.removeEventListener("pointermove", move);
      chip.removeEventListener("pointerup", end);
      chip.removeEventListener("pointercancel", cancel);
      setDragGhost(null);
    };
    chip.addEventListener("pointermove", move);
    chip.addEventListener("pointerup", end);
    chip.addEventListener("pointercancel", cancel);
  };

  const confirmClose = () => {
    Modal.confirm({
      title: t("signPrint.exitTitle") || "確定要離開列印嗎？",
      content: t("signPrint.exitContent") || "離開後，文件上放置的簽名不會保留。",
      okText: t("signPrint.exitOk") || "離開",
      cancelText: t("signPrint.exitCancel") || "繼續編輯",
      okButtonProps: { danger: true },
      zIndex: MODAL_Z,
      onOk: onClose,
    });
  };

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-label={title}
      data-testid="sign-print-preview"
      style={{ position: "fixed", inset: 0, zIndex: OVERLAY_Z, background: "#e9ecef", display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: 600, color: "#444", marginRight: "auto" }}>{title}</div>
        <Button
          icon={<IconSignature size={18} />}
          onClick={() => request(placeInView)}
          disabled={!ready}
          style={{ height: 40 }}
        >
          {t("signPrint.sign") || "簽名"}
        </Button>
        <Button
          type="primary"
          icon={<IconPrinter size={18} />}
          onClick={handlePrint}
          disabled={!ready}
          style={{ height: 40 }}
        >
          {t("signPrint.print") || "列印"}
        </Button>
        <Button
          aria-label={t("signPrint.close") || "關閉"}
          icon={<IconX size={18} />}
          onClick={confirmClose}
          style={{ height: 40 }}
        />
      </div>

      {signature ? (
        <div
          style={{
            background: "#fffbe6",
            borderBottom: "1px solid #ffe58f",
            padding: "6px 12px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
            color: "#8c6d1f",
          }}
        >
          <div
            data-testid="sign-print-chip"
            role="button"
            aria-label={t("signPrint.signature") || "簽名"}
            onPointerDown={startDrag}
            style={{
              background: "#fff",
              border: "1px dashed #d4b106",
              borderRadius: 6,
              padding: 4,
              cursor: "grab",
              touchAction: "none",
              userSelect: "none",
            }}
          >
            <img src={signature} alt={t("signPrint.signature") || "簽名"} draggable={false} style={{ height: 40, display: "block" }} />
          </div>
          <span>{t("signPrint.dragHint") || "拖曳簽名到文件上"}</span>
        </div>
      ) : null}

      <div ref={scrollRef} style={{ flex: 1, overflow: "auto", padding: 16 }}>
        <iframe
          ref={iframeRef}
          title={title}
          data-testid="sign-print-frame"
          srcDoc={srcDoc}
          onLoad={handleLoad}
          style={{
            display: "block",
            margin: "0 auto",
            width: `${pageWidthMm}mm`,
            maxWidth: "none",
            height,
            border: 0,
            background: "#fff",
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            pointerEvents: dragGhost ? "none" : "auto",
          }}
        />
      </div>

      {dragGhost && signature ? (
        <img
          src={signature}
          alt=""
          style={{
            position: "fixed",
            left: dragGhost.x,
            top: dragGhost.y,
            height: 48,
            transform: "translate(-50%, -50%)",
            opacity: 0.8,
            pointerEvents: "none",
            zIndex: OVERLAY_Z + 1,
          }}
        />
      ) : null}

      {modal}
    </div>,
    document.body,
  );
}

export default SignPrintPreview;
